import { Body, Circle } from "p2";

import { collisionGroups } from "../../config/CollisionGroups";
import { Colors } from "../../config/DesignConstants";
import BaseEntity from "../../core/entity/BaseEntity";
import Entity, { GameEvents } from "../../core/entity/Entity";
import { clampUp, lerp } from "../../core/util/MathUtil";
import {
  createRegularPolygon,
  PolygonShape,
} from "../../core/util/PolygonUtils";
import type { V2d } from "../../core/Vector";
import { V } from "../../core/Vector";
import { DustExplosionEffect } from "../DustExplosionEffect";
import {
  applyColor,
  applyThickness,
  applyWobble,
  FlickerFilter,
  VectorFilter,
} from "../graphics";
import VectorSprite from "../graphics/VectorSprite";
import { isShieldDamager } from "../interfaces/ShieldDamager";
import { LevelEntity } from "../LevelEntity";
import ShieldPulse from "./ShieldPulse";

// Shield constants
export const SHIELD_COLOR = Colors.CYAN;
export const SHIELD_RADIUS = 40;
const SHIELD_SIDES = 24;
const SHIELD_BASE_STROKE_WIDTH = 1;
const SHIELD_MAX_STROKE_WIDTH = 3.5;
const SHIELD_HEALTH_THRESHOLD_LOW = 0.3;
const SHIELD_DEFAULT_ALPHA = 1.0;
const SHIELD_HEALTH = 50;

// Hit effect constants
const SHIELD_HIT_EFFECT_DURATION = 0.15; // seconds - used for flash, thickness, and invulnerability
const SHIELD_HIT_THICKNESS_MULTIPLIER = 2.5; // Relative thickness boost when hit
const SHIELD_FLICKER_FREQUENCY = 8; // flickers per second when low health

// Wobble effect constants
const SHIELD_WOBBLE_AMPLITUDE = 0.15; // Very subtle wobble for alive feel
const SHIELD_WOBBLE_FREQUENCY = 1.0; // Slower, gentler wobble

// Shield dust explosion constants
const SHIELD_DUST_PARTICLE_COUNT = 40;
const SHIELD_DUST_EXPLOSION_SPEED = 80;

/**
 * Create shield shape - circular-looking polygon with consistent shield color
 */
function createShieldShape(): PolygonShape {
  return {
    points: createRegularPolygon(SHIELD_SIDES, SHIELD_RADIUS),
    color: SHIELD_COLOR,
    secondaryColor: SHIELD_COLOR,
  };
}

/**
 * Get the color of a damager entity for dust explosion effects
 */
function getDamagerColor(damager: unknown): number {
  // Try to get color from various entity types
  if (damager && typeof damager === "object" && "getVisualConfig" in damager) {
    return (damager as any).getVisualConfig?.().shape?.color || Colors.WHITE;
  }
  return Colors.WHITE; // Default fallback
}

export default class Shield extends LevelEntity implements Entity {
  private shieldRenderer: VectorSprite;

  // Shield state management
  private maxHealth: number = SHIELD_HEALTH;
  private currentHealth: number = SHIELD_HEALTH;

  // Hit effect timing
  private isInvulnerable: boolean = false;
  private flickerFilter: FlickerFilter;

  constructor() {
    super();
    this.tags = ["shield"];

    // Get shield shape and create renderer with base thickness
    const shieldShape = createShieldShape();
    this.shieldRenderer = this.addChild(
      VectorSprite.fromPolygon(
        shieldShape.points,
        shieldShape.color,
        SHIELD_BASE_STROKE_WIDTH,
      ),
    );

    this.shieldRenderer.setAlpha(SHIELD_DEFAULT_ALPHA);

    // Set thickness based on health
    this.shieldRenderer.addFilter(
      new VectorFilter(
        applyThickness(() => {
          return lerp(
            SHIELD_BASE_STROKE_WIDTH,
            SHIELD_MAX_STROKE_WIDTH,
            this.currentHealth / this.maxHealth,
          );
        }),
      ),
    );

    // Create and add flicker filter
    this.flickerFilter = this.shieldRenderer.addFilter(
      new FlickerFilter(SHIELD_FLICKER_FREQUENCY, 0.3),
    );

    // Add gentle wobble for alive feel
    this.shieldRenderer.addFilter(
      new VectorFilter(
        applyWobble(SHIELD_WOBBLE_AMPLITUDE, SHIELD_WOBBLE_FREQUENCY),
      ),
    );

    // Add physics body for collision detection
    this.body = new Body({ mass: 0, type: Body.STATIC });
    const shape = new Circle({ radius: SHIELD_RADIUS });
    shape.collisionGroup = collisionGroups("Turret");
    shape.collisionMask = collisionGroups("Bomb", "Enemy");
    this.body.addShape(shape);
    this.body.position = [0, 0];
  }

  onBeginContact({ other }: GameEvents["beginContact"]) {
    if (isShieldDamager(other)) {
      // Get positions for effects
      const shieldPosition = this.getPosition();
      const damagerPosition =
        other instanceof BaseEntity ? other.getPosition() : V(0, 0);
      const outwardDirection = damagerPosition.sub(shieldPosition).normalize();

      // Always create dust explosion and handle damager response
      this.createDustExplosion(damagerPosition, outwardDirection, other);
      other.onShieldHit();

      if (!this.isInvulnerable) {
        // Full hit response: damage + effects
        const damage = other.getShieldDamage();
        this.takeDamage(damage);
        this.applyHitEffects();
        this.getCurrentLevel()?.addChild(new ShieldPulse(this.getPosition()));
      }
    }
  }

  private takeDamage(damage: number) {
    if (!this.game) return;

    this.currentHealth = clampUp(this.currentHealth - damage);
    const healthPercent = this.currentHealth / this.maxHealth;

    // Update flicker intensity based on health percentage
    this.updateFlickerIntensity(healthPercent);

    if (this.currentHealth <= 0) {
      this.die();
    }
  }

  private createDustExplosion(
    damagerPosition: V2d,
    outwardDirection: V2d,
    damager: unknown,
  ): void {
    const shieldColor = SHIELD_COLOR;
    const damagerColor = getDamagerColor(damager);

    this.getCurrentLevel()?.addChild(
      new DustExplosionEffect({
        position: damagerPosition,
        colors: [shieldColor, damagerColor],
        particleCount: SHIELD_DUST_PARTICLE_COUNT,
        explosionSpeed: SHIELD_DUST_EXPLOSION_SPEED,
        direction: outwardDirection,
      }),
    );
  }

  private applyHitEffects() {
    if (!this.game) return;

    // Apply flash and thicken effects using single VectorFilter (like Asteroid.ts)
    this.shieldRenderer.addFilter(
      new VectorFilter(
        [
          applyColor(Colors.WHITE),
          applyThickness(
            (oldThickness) => oldThickness * SHIELD_HIT_THICKNESS_MULTIPLIER,
          ),
        ],
        SHIELD_HIT_EFFECT_DURATION,
      ),
    );

    // Start invulnerability period using BaseEntity timer
    this.isInvulnerable = true;
    this.wait(SHIELD_HIT_EFFECT_DURATION).then(() => {
      this.isInvulnerable = false;
    });
  }

  private updateFlickerIntensity(healthPercent: number) {
    // Calculate flicker intensity based on how low health is
    // 0 intensity when above threshold, 1.0 intensity when at 0 health
    if (healthPercent >= SHIELD_HEALTH_THRESHOLD_LOW) {
      this.flickerFilter.intensity = 0; // No flicker when healthy
    } else {
      // Linear interpolation: full health at threshold = 0 intensity, 0 health = 1.0 intensity
      this.flickerFilter.intensity =
        1 - healthPercent / SHIELD_HEALTH_THRESHOLD_LOW;
    }
  }

  private die() {
    console.log("Shield destroyed! Game Over!");

    // Dispatch game over event
    this.dispatch("gameOver", {});

    this.destroy();
  }
}
